import { useEffect, useRef, useState } from 'react';
import { motion, useInView } from 'framer-motion';
import { Info, Flame, BarChart3, Zap, ChevronRight, TrendingUp } from 'lucide-react';
import { activeWeeks } from '../utils/trendWeeks';

const SIZE = 216;
const R = 92;
const C = 2 * Math.PI * R;

function useCountUp(target, active, duration = 1500) {
  const [v, setV] = useState(0);
  const raf = useRef(null);
  useEffect(() => {
    const to = Number(target) || 0;
    if (!active) { setV(to); return; }
    let start = null;
    const tick = (now) => {
      if (start === null) start = now;
      const t = Math.min(1, (now - start) / duration);
      setV(Math.round(to * (1 - Math.pow(1 - t, 3))));
      if (t < 1) raf.current = requestAnimationFrame(tick);
    };
    raf.current = requestAnimationFrame(tick);
    return () => raf.current && cancelAnimationFrame(raf.current);
  }, [target, active, duration]);
  return v;
}

/** Smooth curve through the weekly points (quadratic through midpoints). */
function smoothPath(pts) {
  if (pts.length < 2) return '';
  let d = `M ${pts[0].x} ${pts[0].y}`;
  for (let i = 1; i < pts.length - 1; i++) {
    const mx = (pts[i].x + pts[i + 1].x) / 2;
    const my = (pts[i].y + pts[i + 1].y) / 2;
    d += ` Q ${pts[i].x} ${pts[i].y} ${mx} ${my}`;
  }
  const last = pts[pts.length - 1];
  d += ` Q ${last.x} ${last.y} ${last.x} ${last.y}`;
  return d;
}

const MiniStat = ({ icon: Icon, value, label, sub, tint, testId }) => (
  <div
    className={`relative rounded-2xl border px-3 py-3 ${tint
      ? 'border-orange-500/25 bg-gradient-to-b from-orange-500/[0.14] to-orange-500/[0.03]'
      : 'border-white/[0.09] bg-white/[0.04]'}`}
    data-testid={testId}
  >
    <div className="flex items-center justify-between">
      <span className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full ${tint ? 'bg-orange-500/25 text-orange-400' : 'bg-white/[0.08] text-white/65'}`}>
        <Icon size={14} fill={tint ? 'currentColor' : 'none'} />
      </span>
      <ChevronRight size={13} className="shrink-0 text-white/25" />
    </div>
    <span className="mt-1.5 block truncate font-heading text-[22px] font-black leading-none text-white tabular-nums">{value}</span>
    <p className="mt-2 text-[11px] font-medium leading-tight text-white/55">
      {label}{sub && <><br />{sub}</>}
    </p>
  </div>
);

/** Calories Burned: animated ring, smooth six-week trend and session stats. */
export function CaloriesCard({ stats, isDemo }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, amount: 0.25 });

  const total = stats.week_total_cal || 0;
  const trend = activeWeeks((stats.weekly_trend || []).slice(-6), 'calories');
  const prev = [...trend.slice(0, -1)].reverse().find((t) => t.calories > 0)?.calories || 0;
  const pct = prev > 0 ? Math.round(((total - prev) / prev) * 100) : null;
  const sessions = stats.sessions_this_week || 0;
  const avg = sessions > 0 ? Math.round(total / sessions) : 0;

  const count = useCountUp(total, inView);
  const peak = Math.max(...trend.map((t) => t.calories), 1);
  const ratio = Math.min(1, total / peak);

  const pts = trend.map((t, i) => ({
    x: trend.length > 1 ? 4 + (i / (trend.length - 1)) * 92 : 50,
    y: 44 - (t.calories / peak) * 32,
    ...t,
  }));

  return (
    <div ref={ref} className="relative z-[1] px-4 pb-4 pt-4" data-testid="calories-card-body">
      {/* Header */}
      <div className="flex items-start gap-3">
        <span className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl p-[2px]"
              style={{ backgroundImage: 'linear-gradient(135deg,#FFE07A 0%,#F97316 50%,#B91C1C 100%)', boxShadow: '0 0 22px rgba(249,115,22,0.35)' }}>
          <img
            src="/calories-icon.webp"
            alt=""
            width={56}
            height={56}
            loading="lazy"
            decoding="async"
            className="h-full w-full rounded-[14px] object-cover"
            data-testid="calories-header-icon"
          />
        </span>
        <div className="min-w-0 flex-1 pt-0.5">
          <h3 className="font-heading text-[26px] font-black leading-none tracking-tight">
            <span className="text-white">Calories </span>
            <span className="bg-clip-text text-transparent" style={{ backgroundImage: 'linear-gradient(90deg,#FFB020,#F97316)' }}>Burned</span>
          </h3>
          <p className="mt-1.5 text-[13px] text-white/50">Total calories this week</p>
        </div>
        <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-white/[0.12] bg-white/[0.05] text-white/55" data-testid="calories-info-btn">
          <Info size={15} />
        </span>
      </div>

      {isDemo && (
        <span className="absolute right-4 top-[72px] rounded bg-orange-500/15 px-2 py-0.5 font-heading text-[10px] font-black uppercase tracking-wider text-orange-400">Demo</span>
      )}

      {/* Ring */}
      <div className="relative mx-auto mt-2" style={{ width: SIZE, height: SIZE }} data-testid="calories-ring">
        <span className="pointer-events-none absolute inset-[13px] rounded-full border border-white/[0.07]"
              style={{ background: 'radial-gradient(circle at 50% 30%, rgba(255,255,255,0.06) 0%, rgba(10,10,10,0.9) 55%, rgba(0,0,0,0.95) 100%)' }} />
        <motion.span
          className="pointer-events-none absolute inset-8 rounded-full"
          style={{ background: 'radial-gradient(circle, rgba(249,115,22,0.25) 0%, transparent 70%)' }}
          animate={{ opacity: [0.45, 1, 0.45], scale: [0.95, 1.05, 0.95] }}
          transition={{ duration: 3.4, repeat: Infinity, ease: 'easeInOut' }}
        />
        <svg viewBox={`0 0 ${SIZE} ${SIZE}`} className="absolute inset-0 h-full w-full -rotate-90">
          <defs>
            <linearGradient id="calRing" x1="0" y1="1" x2="1" y2="0">
              <stop offset="0%" stopColor="#EA580C" />
              <stop offset="55%" stopColor="#F97316" />
              <stop offset="88%" stopColor="#FFC83D" />
              <stop offset="100%" stopColor="#FFE07A" />
            </linearGradient>
          </defs>
          <circle cx={SIZE / 2} cy={SIZE / 2} r={R} fill="none" stroke="rgba(255,255,255,0.05)" strokeWidth="16" />
          <motion.circle
            cx={SIZE / 2} cy={SIZE / 2} r={R} fill="none" stroke="url(#calRing)" strokeWidth="16" strokeLinecap="round"
            strokeDasharray={C}
            initial={{ strokeDashoffset: C }}
            animate={inView ? { strokeDashoffset: C * (1 - ratio) } : { strokeDashoffset: C }}
            transition={{ duration: 1.6, ease: [0.22, 1, 0.36, 1] }}
            style={{ filter: 'drop-shadow(0 0 14px rgba(249,115,22,0.8))' }}
          />
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <span className="font-heading text-[58px] font-black leading-[0.9] text-white tabular-nums"
                style={{ textShadow: '0 2px 18px rgba(0,0,0,0.6)' }} data-testid="calories-counter">
            {count.toLocaleString()}
          </span>
          <span className="mt-1 font-heading text-base font-bold uppercase tracking-[0.2em] text-white/40">kcal</span>
          {total === 0 ? (
            <span className="mt-2.5 rounded-full border border-white/[0.08] bg-black/70 px-3 py-1.5 text-[12px] text-white/45 backdrop-blur-sm"
                  data-testid="calories-empty-note">
              No sessions logged yet this week
            </span>
          ) : pct !== null && (
            <span className="mt-2.5 flex items-center gap-1.5 rounded-full border border-white/[0.08] bg-black/70 px-3 py-1.5 backdrop-blur-sm"
                  data-testid="calories-delta">
              <TrendingUp size={13} className={pct >= 0 ? 'text-accent' : 'rotate-90 text-white/45'} strokeWidth={3} />
              <span className={`font-heading text-sm font-black ${pct >= 0 ? 'text-accent' : 'text-white/70'}`}>
                {pct >= 0 ? '+' : ''}{pct}%
              </span>
              <span className="text-[12px] text-white/45">vs last week</span>
            </span>
          )}
        </div>
      </div>

      {/* Six-week trend */}
      {trend.length > 1 && (
        <div className="mt-3" data-testid="calories-trend">
          <div className="relative h-[72px] w-full">
            <svg viewBox="0 0 100 50" preserveAspectRatio="none" className="absolute inset-0 h-full w-full">
              <defs>
                <clipPath id="calReveal">
                  <motion.rect
                    x="0" y="0" height="50"
                    initial={{ width: 0 }}
                    animate={inView ? { width: 100 } : { width: 0 }}
                    transition={{ duration: 1.7, ease: 'easeInOut' }}
                  />
                </clipPath>
              </defs>
              {/* dotted drop lines */}
              {pts.map((p) => (
                <line
                  key={`d-${p.label}`}
                  x1={p.x} y1={p.y} x2={p.x} y2="50"
                  stroke={p.is_current ? 'rgba(249,115,22,0.65)' : 'rgba(249,115,22,0.3)'}
                  strokeWidth="0.6" strokeDasharray="1.4 1.8" vectorEffect="non-scaling-stroke"
                />
              ))}
              {/* glow that traces the exact curve: wide soft strokes under a crisp line */}
              <g clipPath="url(#calReveal)">
                {[
                  { w: 12, o: 0.08 },
                  { w: 8, o: 0.14 },
                  { w: 5, o: 0.22 },
                  { w: 3, o: 0.35 },
                ].map((g) => (
                  <path
                    key={g.w}
                    d={smoothPath(pts)} fill="none" stroke="#FB923C" strokeWidth={g.w}
                    strokeLinejoin="round" strokeLinecap="round" opacity={g.o}
                    vectorEffect="non-scaling-stroke"
                  />
                ))}
                <path
                  d={smoothPath(pts)} fill="none" stroke="#FFB357" strokeWidth="2" strokeLinejoin="round" strokeLinecap="round"
                  style={{ filter: 'drop-shadow(0 0 3px rgba(249,115,22,0.95))' }}
                  vectorEffect="non-scaling-stroke"
                />
              </g>
            </svg>
            <motion.span
              className="pointer-events-none absolute bottom-0 w-11 rounded-t-xl"
              style={{
                left: `${pts[pts.length - 1].x}%`,
                top: `${(pts[pts.length - 1].y / 50) * 100}%`,
                transform: 'translateX(-50%)',
                background: 'linear-gradient(180deg, rgba(249,115,22,0.5) 0%, rgba(249,115,22,0.12) 55%, rgba(249,115,22,0) 100%)',
              }}
              initial={{ opacity: 0, scaleY: 0.3 }}
              animate={inView ? { opacity: 1, scaleY: 1 } : { opacity: 0, scaleY: 0.3 }}
              transition={{ delay: 1.2, duration: 0.6, ease: 'easeOut' }}
              data-testid="calories-now-column"
            />
            {pts.map((p, i) => (
              <motion.span
                key={p.label}
                className={`absolute block rounded-full border-2 ${p.is_current
                  ? 'h-[14px] w-[14px] border-orange-300 bg-orange-500'
                  : 'h-[11px] w-[11px] border-orange-400 bg-[#120B06]'}`}
                style={{
                  left: `${p.x}%`,
                  top: `${(p.y / 50) * 100}%`,
                  transform: 'translate(-50%, -50%)',
                  boxShadow: p.is_current ? '0 0 12px rgba(249,115,22,0.95)' : '0 0 6px rgba(249,115,22,0.45)',
                }}
                initial={{ scale: 0, opacity: 0 }}
                animate={inView ? { scale: 1, opacity: 1 } : { scale: 0, opacity: 0 }}
                transition={{ delay: 0.55 + i * 0.12, type: 'spring', stiffness: 420, damping: 18 }}
              />
            ))}
          </div>
          <div className="mt-1.5 grid" style={{ gridTemplateColumns: `repeat(${trend.length}, minmax(0,1fr))` }}>
            {trend.map((t) => (
              <div key={t.label} className="text-center">
                <p className={`font-heading text-[11px] font-bold uppercase tracking-wider ${t.is_current ? 'text-orange-400' : 'text-white/40'}`}>{t.label}</p>
                <p className={`font-heading text-[15px] font-black tabular-nums ${t.is_current ? 'text-white' : 'text-white/65'}`}>{t.calories}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Session stats */}
      <div className="mt-4 grid grid-cols-3 gap-2.5" data-testid="calories-stats-row">
        <MiniStat icon={Flame} value={stats.last_session_cal || 0} label="Last Session" tint testId="cal-stat-last" />
        <MiniStat icon={BarChart3} value={sessions} label="Sessions" sub="This Week" testId="cal-stat-sessions" />
        <MiniStat icon={Zap} value={avg} label="Avg. per Session" tint testId="cal-stat-avg" />
      </div>

      <p className="mt-3.5 flex items-center justify-center gap-1.5 text-center text-[11px] text-white/35" data-testid="calories-info-note">
        <Info size={12} className="shrink-0" />
        Estimated using MET-based calculation. Results may vary ±15%.
      </p>
    </div>
  );
}
